import { useState } from "react";
import { Chip, Section, SectionHeading } from "@/components/ui";

/**
 * "Pick a path": tap a field to see the careers, the route to get there and what it can pay.
 * One tab is open at a time; the panel is announced politely to screen readers when it changes.
 */
export function IndustryPaths({ paths }) {
  const [active, setActive] = useState(0);
  const tab = paths.tabs[active] ?? paths.tabs[0];

  return (
    <Section tone="brandTint" labelledBy="industry-paths-title" testId="industry-paths">
      <SectionHeading id="industry-paths-title" title={paths.title} intro={paths.lead} />

      <div role="group" aria-label={paths.title} className="mt-6 flex flex-wrap gap-2 sm:mt-8" data-testid="industry-path-tabs">
        {paths.tabs.map((item, i) => (
          <Chip key={item.name} pressed={i === active} onClick={() => setActive(i)} data-testid={`industry-path-tab-${i}`}>
            {item.name}
          </Chip>
        ))}
      </div>

      <div
        aria-live="polite"
        data-testid="industry-path-panel"
        className="mt-5 grid gap-6 rounded-4xl border border-brand-200 bg-white p-5 shadow-soft sm:p-8 lg:grid-cols-12 lg:gap-10"
      >
        <div className="lg:col-span-6">
          <h3 className="font-heading text-xl font-bold sm:text-2xl">{paths.careersLabel}</h3>
          <ul key={tab.name} className="mt-4 flex flex-wrap gap-2">
            {tab.roles.map((role, i) => (
              <li
                key={role}
                style={{ animationDelay: `${i * 60}ms` }}
                className="animate-pop rounded-full border border-brand-200 bg-brand-50 px-4 py-1.5 text-sm font-semibold sm:text-base"
              >
                {role}
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-6">
          <h3 className="font-heading text-xl font-bold sm:text-2xl">{paths.routeLabel}</h3>
          <ol className="relative mt-4 space-y-4">
            <span aria-hidden="true" className="absolute bottom-6 left-[1.05rem] top-6 border-l border-brand-200" />
            {tab.route.map((step, i) => (
              <li key={step} className="relative flex items-start gap-3">
                <span
                  aria-hidden="true"
                  className="relative z-10 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-500 font-heading text-sm font-bold text-white"
                >
                  {i + 1}
                </span>
                <p className="pt-1.5 text-base text-ink/80">{step}</p>
              </li>
            ))}
          </ol>
        </div>

        <div className="rounded-3xl border border-brand-200 bg-brand-50 px-4 py-3 text-base text-ink/80 lg:col-span-12">
          <p>
            <b className="font-bold text-ink">{paths.payLabel}</b>
            {tab.pay}
          </p>
          {paths.payNote ? <p className="mt-2 text-sm text-ink/60">{paths.payNote}</p> : null}
        </div>
      </div>
    </Section>
  );
}
